import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useEffect, useState } from "react";
import { DateInput, Modal, Select, TextInput } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../shared/enums/modalId";
import { handleNarIngestionUpload } from "../../../pages/configurations/pages/narConfiguration/actions/handleNarIngestionUpload";
import useFetchPayerSubtypes from "../../../pages/configurations/pages/narConfiguration/hooks/fetchPayerSubtypes";
import {
  extractInsuranceOptions,
  extractPlanOptions,
} from "../../../pages/configurations/pages/narConfiguration/utils/extractPayerOptions";
import CustomRadioButton from "../../customRadioButton/customRadioButton";
import CustomCheckBox from "../../customCheckBox/customCheckBox";
import DragDropUpload from "../../dragDropUpload/dragDropUpload";
import { ToastMessages } from "../../toasts/toastMessages";
import { getFilePathForIngestion } from "./utils/generateFilePathForIngestion";

interface NarIngestionUploadProps {
  onClose: () => void;
}

const NarIngestionUpload = ({ onClose }: NarIngestionUploadProps) => {
  const [fileType, setFileType] = useState<"pdf" | "csv">("pdf");
  const [selectedInsurance, setSelectedInsurance] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [startPage, setStartPage] = useState<string>("");
  const [endPage, setEndPage] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(
    moment(new Date()).format("MM/DD/YYYY"),
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [neverExpire, setNeverExpire] = useState<boolean>(false);

  const { data: payerSubtypes } = useFetchPayerSubtypes();

  // Extract insurance and plan options using utility functions
  const insuranceOptions = extractInsuranceOptions(payerSubtypes);
  const planOptions = extractPlanOptions(payerSubtypes, selectedInsurance);

  // Clear plan when insurance changes
  useEffect(() => {
    setSelectedPlan("");
  }, [selectedInsurance]);

  // Clear the page range when the file type changes so a stale range is
  // never carried into a PDF submit (inputs are uncontrolled and remount).
  useEffect(() => {
    setStartPage("");
    setEndPage("");
  }, [fileType]);

  const filePath = getFilePathForIngestion(
    selectedInsurance,
    selectedPlan,
    selectedDate,
    fileType,
  );

  const handleUploadSuccess = () => {
    toast.success(ToastMessages.NAR_INGESTION_FILE_UPLOADED_SUCCESS);
  };

  const handleSave = async () => {
    await handleNarIngestionUpload({
      selectedInsurance,
      selectedPlan,
      selectedDate,
      uploadedFiles,
      fileType,
      startPage,
      endPage,
      onSuccess: onClose,
      setIsSubmitting,
      neverExpire,
    });
  };

  return (
    <Modal
      dialogId={ModalId.NAR_INGESTION_UPLOAD_MODAL}
      title="Upload Grid"
      onSave={handleSave}
      saveButtonText={isSubmitting ? "Submitting..." : "Confirm"}
      cancelText="Cancel"
      onClose={onClose}
      onCancel={onClose}
      disableSave={isSubmitting}
    >
      <div className="flex h-full w-full flex-col gap-2">
        <Select
          id="insurance"
          label="Select the Insurance"
          options={insuranceOptions}
          value={selectedInsurance}
          onOptionChange={(option) => {
            setSelectedInsurance(option?.value || "");
          }}
          placeholder="Select the Insurance"
        />
        <Select
          key={selectedInsurance}
          id="plan"
          label="Select the Plan"
          options={planOptions}
          value={selectedPlan || undefined}
          onOptionChange={(option) => {
            setSelectedPlan(option?.value || "");
          }}
          placeholder="Select the Insurance first to see the plans"
        />
        <div className="w-full overflow-x-hidden">
          <DateInput
            id="date"
            label="Select the Date"
            format="MM/DD/YYYY"
            defaultValue={moment(new Date()).format("MM/DD/YYYY")}
            onChange={(data) => {
              // Format the date to ensure consistent MM/DD/YYYY format
              if (data.value) {
                const date = moment(data.value);
                if (date.isValid()) {
                  setSelectedDate(date.format("MM/DD/YYYY"));
                } else {
                  setSelectedDate(data.value);
                }
              } else {
                setSelectedDate("");
              }
            }}
          />
        </div>
        <div className="nar-ingestion-upload__file-type flex flex-col gap-2">
          <div className="text-x-tiny">Select the File Type</div>
          <div className="flex flex-row gap-2">
            <CustomRadioButton
              name="fileType"
              value="pdf"
              checked={fileType === "pdf"}
              label="Pdf"
              onChange={() => setFileType("pdf")}
            />
            <CustomRadioButton
              name="fileType"
              value="csv"
              checked={fileType === "csv"}
              label="Csv"
              onChange={() => setFileType("csv")}
            />
          </div>
        </div>
        {fileType === "pdf" && (
          <div className="nar-ingestion-upload__page-range flex flex-row gap-2">
            <TextInput
              id="start-page"
              label="Start Page"
              type="number"
              placeholder="Start page (1-indexed)"
              onChange={(e) => setStartPage(e.value)}
            />
            <TextInput
              id="end-page"
              label="End Page"
              type="number"
              placeholder="End page (1-indexed)"
              onChange={(e) => setEndPage(e.value)}
            />
          </div>
        )}
        <CustomCheckBox
          id="never-expire"
          label="Never expire (rows stay valid instead of ending at month-end)"
          isChecked={neverExpire}
          onCheckBoxValueChange={(data) => setNeverExpire(data.value)}
        />
        <DragDropUpload
          id="nar-ingestion-upload"
          subText="Drag and drop or click to upload a new file"
          allowedFileTypes={[".pdf", ".csv"]}
          maxFileSize={10 * 1024 * 1024}
          filePath={filePath}
          onUploadSuccess={handleUploadSuccess}
          downloadedURL={(urls) => setUploadedFiles(urls)}
        />
      </div>
    </Modal>
  );
};

export default NarIngestionUpload;
